import { createApplication } from '@angular/platform-browser';
import { createCustomElement } from '@angular/elements';
import { appConfig } from './app/app.config';
import { SearchComponent } from 'search/search.component';

function injectLinkOnce(href: string, dataAttr: string) {
  if (!document.querySelector(`link[${dataAttr}]`)) {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    link.setAttribute(dataAttr, '');
    document.head.appendChild(link);
  }
}

function injectStyleOnce(css: string, dataAttr: string) {
  if (!document.querySelector(`style[${dataAttr}]`)) {
    const style = document.createElement('style');
    style.textContent = css;
    style.setAttribute(dataAttr, '');
    document.head.appendChild(style);
  }
}

function setCssVar(name: string, value?: string | null) {
  if (value && value.trim()) {
    document.documentElement.style.setProperty(name, value.trim());
  }
}

function getAttr(el: Element, name: string): string | null {
  const v = el.getAttribute(name);
  return v && v.trim() ? v.trim() : null;
}

function applyBrandVarsFromWidget() {
  const widget = document.querySelector('search-widget') as HTMLElement | null;
  if (!widget) return;

  const secondary = getAttr(widget, 'buttons-color-text');
  setCssVar('--color-secondary-600', secondary);
  const customBase = getAttr(widget, 'data-custom');
  setCssVar('--color-custom', customBase);

  const scaleAttrs = [
    ['--border-color', 'border-color'], // borde general
    ['--background-color', 'background-color'], // fondos
    ['--title-color', 'title-color'], // título
    ['--chip-selected-color', 'chip-selected-color'], // chips color
    ['--input-border-color', 'input-border-color'], //inputs
    ['--search-button-color', 'search-button-color'], // botón buscador
    ['--chip-text-color', 'chip-text-color'], // texto chips
    ['--chip-selected-text', 'chip-selected-text'], // texto chips select
    ['--placeholder-color', 'placeholder-color'], // texto placeholder
  ] as const;

  for (const [cssVar, attr] of scaleAttrs) {
    const val = getAttr(widget, attr);
    setCssVar(cssVar, val);
  }
}

function observeWidgetAttributes() {
  const attachAttrObserver = (el: Element) => {
    const obs = new MutationObserver(() => applyBrandVarsFromWidget());
    obs.observe(el, {
      attributes: true,
      attributeFilter: [
        'buttons-color-text',
        'data-custom',
        'border-color',
        'background-color',
        'title-color',
        'chips-selected-color',
        'input-border-color',
        'search-button-color',
        'chip-text-color',
        'chip-selected-text',
        'placeholder-color',
      ],
    });
  };

  const existing = document.querySelector('search-widget');
  if (existing) attachAttrObserver(existing);

  const rootObs = new MutationObserver((_mutations, observer) => {
    const w = document.querySelector('search-widget');
    if (w) {
      applyBrandVarsFromWidget();
      attachAttrObserver(w);
      observer.disconnect();
    }
  });
  rootObs.observe(document.documentElement, { subtree: true, childList: true });
}

createApplication(appConfig)
  .then((appRef) => {
    const injector = appRef.injector;
    const el = createCustomElement(SearchComponent, { injector });

    if (!customElements.get('search-widget')) {
      customElements.define('search-widget', el);
    }

    const stylesHref = new URL('./styles.css', import.meta.url).toString();
    injectLinkOnce(stylesHref, 'data-search-widget-theme');

    const symbolsHref =
      'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@20..48,100..700,0..1,-50..200';
    injectLinkOnce(symbolsHref, 'data-material-symbols');
    injectStyleOnce(
      `
      .material-symbols-outlined{
        font-variation-settings:'FILL' 0,'wght' 400,'GRAD' 0,'opsz' 24;
        line-height:1;
        vertical-align:middle;
      }
    `,
      'data-material-symbols-defaults'
    );
    applyBrandVarsFromWidget();
    observeWidgetAttributes();
  })
  .catch(console.error);
